import { cn } from '@moment/design-system-ui/lib/cn'
import { type InputHTMLAttributes, type Ref, useId, useState } from 'react'

import { EyeIcon } from '#components/_primitives/icons/eye-icon'
import { EyeSlashIcon } from '#components/_primitives/icons/eye-slash-icon'
import { Text } from '#components/controls/text'

interface ErrorTextProps {
  error: string | undefined
  id?: string
  className?: string
  'data-testid'?: string
}

export const ErrorText = ({
  error,
  id,
  className,
  'data-testid': dataTestid,
}: ErrorTextProps) => (
  <Text
    variant="base"
    id={id}
    className={cn('text-(--color-text-danger)', className)}
    role="alert"
    aria-live="polite"
    data-testid={dataTestid}
  >
    {error}
  </Text>
)

interface InputStyleOptions {
  disabled?: boolean
  hasError?: boolean
  className?: string
}

export const getInputStyles = ({
  disabled,
  hasError,
  className,
}: InputStyleOptions = {}) => {
  return cn(
    // Typography
    'font-[family-name:var(--font-sans)] tracking-(--tracking-base-dark)',

    // Text colors (light and dark mode)
    disabled
      ? 'text-(--color-text-tertiary) dark:text-(--color-text-secondary-contrast)'
      : 'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)',

    // Placeholder styling
    'placeholder:text-(--color-text-tertiary) dark:placeholder:text-(--color-text-secondary-contrast)',

    // Background colors and hover states
    'bg-(--color-surface-primary) hover:bg-(--color-surface-secondary)/40 focus:hover:bg-(--color-surface-primary)',
    'dark:bg-(--color-surface-secondary-contrast) dark:hover:bg-(--color-surface-secondary-contrast)/60 dark:focus:bg-(--color-surface-primary-contrast)',

    // Caret styling
    'caret-(--color-surface-accent) dark:caret-(--color-border-accent)',

    // Border styling
    'border rounded-[0.625rem]',
    hasError
      ? 'border-(--color-border-danger) focus:border-(--color-border-danger)'
      : 'border-(--color-border-primary) focus:border-(--color-border-secondary) dark:border-(--color-border-primary-contrast)/80 dark:focus:border-(--color-border-accent)',

    // Shadow effects
    'shadow-[0px_2px_8px_0px_rgba(0,0,0,0.01)] focus:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.03)]',
    'dark:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.03)] dark:focus:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.05)]',
    'disabled:shadow-none dark:disabled:shadow-none',

    // Transitions and interactions
    'transition-all duration-250 ease',
    'focus:outline-none',

    // Disabled states
    'disabled:cursor-not-allowed',
    'disabled:bg-(--color-surface-secondary)',
    'dark:disabled:bg-(--color-surface-secondary-contrast)/25 dark:disabled:border-(--color-border-primary-contrast)/40',

    // Custom classes
    className,
  )
}

interface SimpleInputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>
  label?: string
  type?: 'email' | 'password' | 'text'
  error?: string
  disabled?: boolean
}

export const SimpleInput = ({
  ref,
  label,
  type = 'text',
  error,
  disabled,
  className,
  id,
  ...props
}: SimpleInputProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const [inputType, setInputType] = useState(type)
  const generatedId = useId()
  const inputId = id || generatedId
  const errorId = `${inputId}-error`

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
    setInputType(showPassword ? 'password' : 'text')
  }

  const isPassword = type === 'password'

  return (
    <>
      {label && (
        <Text
          as="label"
          variant="base"
          className={cn(
            'block mb-1.5',
            'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)',
          )}
          htmlFor={inputId}
        >
          {label}
        </Text>
      )}

      {/* Password managers modify this div, causing hydration warnings */}
      <div className="relative">
        <input
          id={inputId}
          ref={ref}
          type={inputType}
          disabled={disabled}
          className={cn(
            // Base layout and sizing - specific to regular inputs
            'w-full',
            // Dynamic padding based on password visibility
            isPassword ? 'pr-10' : 'pr-3',
            'px-4 pt-2.5 pb-2.25',
            // Typography specific to regular inputs
            'text-[length:var(--text-base)] leading-(--leading-base) font-[number:var(--font-weight-light)]',

            // Shared input styles
            getInputStyles({ disabled, hasError: Boolean(error), className }),
          )}
          autoCorrect="off"
          autoComplete="on"
          spellCheck="false"
          {...props}
        />

        {isPassword && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            disabled={disabled}
            className={cn(
              'absolute inset-y-0 right-2 top-1/2 -translate-y-1/2',
              'rounded-full',
              'p-2 flex items-center h-fit',
              'text-(--color-text-tertiary)',
              'transition-colors duration-250 ease',
              'hover:text-(--color-text-secondary)/80 dark:hover:text-(--color-text-tertiary)/75',
              'cursor-pointer disabled:cursor-not-allowed',
            )}
          >
            {showPassword ? (
              <EyeSlashIcon className="h-3.5 w-3.5" />
            ) : (
              <EyeIcon className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {error && <ErrorText error={error} id={errorId} className="mt-1.5" />}
    </>
  )
}
